/**
 * In-flight request deduplication + debounce helpers.
 *
 * deduplicatedGet(axios, url)
 *   - If an identical request is already in-flight, returns the same Promise
 *     instead of firing a second network request. Prevents duplicate API calls
 *     when multiple components mount at the same time.
 *
 * useDebouncedValue(value, delay)
 *   - React hook that returns a debounced copy of `value`.
 *   - Use on search inputs, filter dropdowns, and time-range selectors so that
 *     rapid changes (e.g. typing) don't fire a new request on every keystroke.
 *
 * Usage:
 *   import { deduplicatedGet } from '../utils/fetchDebounce';
 *   import { useDebouncedValue } from '../utils/fetchDebounce';
 *
 *   // In a component
 *   const debouncedSearch = useDebouncedValue(searchTerm, 400);
 *   useEffect(() => { fetchData(debouncedSearch); }, [debouncedSearch]);
 *
 *   // Replace axios.get with:
 *   const data = await deduplicatedGet(axios, url);
 */

import { useState, useEffect, useRef } from 'react';

// Map of url → in-flight Promise
const inFlight = new Map();

/**
 * Deduplicating axios GET. If the same URL is already being fetched,
 * returns the existing Promise instead of starting a new request.
 */
export async function deduplicatedGet(axiosInstance, url, config = {}) {
  if (inFlight.has(url)) {
    return inFlight.get(url);
  }

  const promise = axiosInstance.get(url, config)
    .then(res => res.data)
    .finally(() => inFlight.delete(url));

  inFlight.set(url, promise);
  return promise;
}

/**
 * React hook that debounces a value.
 * Returns the debounced value only after `delay` ms of no changes.
 */
export function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

/**
 * React hook that returns a stable debounced callback.
 * The callback won't fire until `delay` ms after the last call.
 */
export function useDebouncedCallback(fn, delay = 400) {
  const timerRef = useRef(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  return (...args) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fnRef.current(...args), delay);
  };
}
